/**
 * The one place a subscription plan's price lives.
 *
 * Checkout (`resolveStripePrice`) refuses a Stripe price whose amount or
 * cadence differs from this book, and the public pricing copy reads it, so
 * the Stripe dashboard, the checkout gate and the marketing pages can never
 * disagree. A plan whose price the owner has not decided yet is
 * `pending_owner_decision`: it is a valid plan key for entitlement purposes,
 * but checkout refuses it before any Stripe call, so nothing can be sold at
 * a guessed price.
 *
 * Pure and dependency-free (type imports only) so the Control Plane and the
 * client bundle can both import it.
 */

import type { BillingCycle, BillingPlanKey } from "./plans";

export type BillingPlanPrice =
  | {
      status: "offered";
      /** USD cents per cycle; the Stripe price must match exactly. */
      cents: Readonly<Record<BillingCycle, number>>;
    }
  | {
      status: "pending_owner_decision";
      /** Monthly USD cents under consideration. Never used to sell. */
      candidateMonthlyCents: readonly number[];
    };

export const BILLING_PLAN_PRICEBOOK = {
  // Vault: memory, database and network as one bundle, with 200 GB of cloud
  // file storage. Owner decision 2026-10-13.
  vault: { status: "offered", cents: { monthly: 2_000, yearly: 20_000 } },
  executive: { status: "offered", cents: { monthly: 8_000, yearly: 80_000 } },
  business: { status: "offered", cents: { monthly: 25_000, yearly: 250_000 } },
} as const satisfies Readonly<Record<BillingPlanKey, BillingPlanPrice>>;

/** The price checkout must charge, or `null` while the plan is not on sale. */
export function billingPlanPriceCents(plan: BillingPlanKey, cycle: BillingCycle): number | null {
  const price: BillingPlanPrice = BILLING_PLAN_PRICEBOOK[plan];
  return price.status === "offered" ? price.cents[cycle] : null;
}

/** Whether checkout may sell this plan at all. */
export function isBillingPlanOffered(plan: BillingPlanKey): boolean {
  return BILLING_PLAN_PRICEBOOK[plan].status === "offered";
}

/** Published prices of the plans that are on sale, for pricing copy. */
export const BILLING_PRICE_CENTS = {
  vault: BILLING_PLAN_PRICEBOOK.vault.cents,
  executive: BILLING_PLAN_PRICEBOOK.executive.cents,
  business: BILLING_PLAN_PRICEBOOK.business.cents,
} as const;

/**
 * A lower price for a new subscriber's first billing periods, after which the
 * plan's regular price applies. Checkout attaches it as a repeating Stripe
 * coupon that takes `regular - intro` off each of the first `months`
 * invoices, so the Stripe price stays the regular one and the price gate
 * above is unchanged.
 */
export type BillingIntroOffer = {
  plan: BillingPlanKey;
  /** Only monthly: a repeating coupon on a yearly price would discount one $800 invoice. */
  cycle: "monthly";
  /** USD cents charged for each intro month. */
  introCents: number;
  /** How many monthly invoices get the intro price. */
  months: number;
  /** The Stripe coupon ID; checkout creates it on first use and refuses one that differs. */
  stripeCouponId: string;
};

/**
 * The offers on sale. Owner decision 2026-10-22: Professional is $45 a month
 * for the first three months, then $80. Empty the list to end the offer;
 * subscriptions that already started it keep their coupon until it runs out.
 */
export const BILLING_INTRO_OFFERS: readonly BillingIntroOffer[] = [
  {
    plan: "executive",
    cycle: "monthly",
    introCents: 4_500,
    months: 3,
    stripeCouponId: "professional_intro_4500_3mo",
  },
];

/** The intro offer for a plan and cycle, or null when it is sold at its regular price. */
export function billingIntroOffer(plan: BillingPlanKey, cycle: BillingCycle): BillingIntroOffer | null {
  return BILLING_INTRO_OFFERS.find((offer) => offer.plan === plan && offer.cycle === cycle) ?? null;
}

/** What the offer's coupon takes off each intro invoice, in USD cents. */
export function introOfferDiscountCents(offer: BillingIntroOffer): number {
  const regular = billingPlanPriceCents(offer.plan, offer.cycle);
  if (regular === null || offer.introCents <= 0 || offer.introCents >= regular) {
    throw new Error(`The ${offer.plan} intro offer must be below the plan's regular price.`);
  }
  return regular - offer.introCents;
}

type IntroOfferPayments = {
  plan?: unknown;
  subscriptionId?: unknown;
  customerId?: unknown;
  stripeCustomerId?: unknown;
} | null | undefined;

/**
 * Intro prices are for first subscriptions. Stripe creates the customer when
 * a subscription checkout completes, so an account with a stored customer or
 * subscription has subscribed before and pays the regular price. Lifetime
 * accounts never check out. The client shows the offer by this rule and the
 * checkout route applies it by the same rule.
 *
 * Accepted limit of this short-term promotion: the rule is per account, not
 * per person or card. Someone who deletes an account and signs up again, or
 * signs up under a second email, gets the intro price again, and two
 * checkouts opened before either completes both carry the coupon (the
 * existing-subscription audit only sees a stored customer). Each costs at
 * most $105 of discount; a per-card or per-customer redemption limit is the
 * fix if the offer becomes permanent.
 */
export function isEligibleForIntroOffer(payments: IntroOfferPayments): boolean {
  if (payments?.plan === "lifetime") return false;
  const customerId = String(payments?.customerId || payments?.stripeCustomerId || "").trim();
  const subscriptionId = String(payments?.subscriptionId || "").trim();
  return !customerId && !subscriptionId;
}

/** Bytes in one storage gigabyte, matching the binary units storage limits use. */
export const STORAGE_GIGABYTE_BYTES = 1024 * 1024 * 1024;

/** Cloud file storage the Vault plan includes before any add-on. */
export const VAULT_INCLUDED_STORAGE_GB = 200;

/**
 * The Vault storage add-on: a recurring Stripe price, added to the Vault
 * subscription as a second item whose quantity is the number of units. Each
 * unit raises the Vault's hard storage limit by `gigabytesPerUnit`. Stripe
 * requires every item on a subscription to share one billing interval, so the
 * add-on has one price per cycle and a Vault carries the price matching its
 * own cycle: $5 a month, or $60 a year (the same rate, no yearly discount).
 * The units route refuses a Stripe price that differs from this entry.
 */
export const VAULT_STORAGE_ADDON = {
  gigabytesPerUnit: 200,
  prices: {
    monthly: { lookupKey: "vault_storage_200gb_monthly", cents: 500 },
    yearly: { lookupKey: "vault_storage_200gb_yearly", cents: 6_000 },
  },
} as const satisfies {
  gigabytesPerUnit: number;
  prices: Record<BillingCycle, { lookupKey: string; cents: number }>;
};

/** The most add-on units one account may hold (10 TB on top of the plan). */
export const MAX_VAULT_STORAGE_ADDON_UNITS = 50;

export function isVaultStorageAddonUnits(value: unknown): value is number {
  return typeof value === "number"
    && Number.isSafeInteger(value)
    && value >= 0
    && value <= MAX_VAULT_STORAGE_ADDON_UNITS;
}

/** The Vault's hard cloud storage limit with `addonUnits` add-on units. */
export function vaultStorageLimitBytes(addonUnits: number): number {
  const units = isVaultStorageAddonUnits(addonUnits) ? addonUnits : 0;
  return (VAULT_INCLUDED_STORAGE_GB + units * VAULT_STORAGE_ADDON.gigabytesPerUnit) * STORAGE_GIGABYTE_BYTES;
}

/** A USD amount for pricing copy: whole dollars as "$20" or "$2,500", otherwise "$4.50". */
export function formatUsdCents(cents: number): string {
  const whole = cents % 100 === 0;
  return `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })}`;
}

/** What paying yearly saves against twelve monthly payments, in USD cents. */
export function yearlySavingCents(plan: keyof typeof BILLING_PRICE_CENTS): number {
  const cents = BILLING_PRICE_CENTS[plan];
  return Math.max(0, cents.monthly * 12 - cents.yearly);
}
